import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BONE_INDEX, makeRig, REF_HEIGHT } from '../chars/rig.js';
import { specFor } from '../chars/variants.js';

/**
 * Crowd archetype geometry.
 *
 * A crowd instance must be ONE buffer — body plus weapon — or every soldier
 * costs two draw calls. The weapon is therefore baked into the body buffer at
 * its bind-pose socket transform and rigid-skinned to the hand bone, so it
 * follows the arm through the vertex-animation texture exactly the way an
 * attached mesh would follow a real bone.
 *
 * ARCHETYPES are the crowd's visual vocabulary: a (kind, geometry variant,
 * weapon) triple. Everything else — faction, colourway, height, animation
 * phase — rides on per-instance data and costs nothing.
 */
export const ARCHES = [
  { key: 'grunt_spear', kind: 'grunt', geo: 0, vat: 0, weapon: 'spear' },
  { key: 'grunt_dao', kind: 'grunt', geo: 1, vat: 0, weapon: 'dao' },
  { key: 'grunt_halberd', kind: 'grunt', geo: 6, vat: 0, weapon: 'halberd' },
  { key: 'grunt_banner', kind: 'grunt', geo: 3, vat: 0, weapon: 'banner' },
  { key: 'archer', kind: 'archer', geo: 0, vat: 1, weapon: 'bow' },
  { key: 'sergeant', kind: 'sergeant', geo: 0, vat: 2, weapon: 'halberd' },
  { key: 'officer', kind: 'officer', geo: 0, vat: 3, weapon: 'dao' },
  { key: 'general', kind: 'general', geo: 0, vat: 4, weapon: 'greatGlaive' },
];
export const ARCH_INDEX = {};
ARCHES.forEach((a, i) => { ARCH_INDEX[a.key] = i; });

/**
 * ARCHETYPE COLLAPSE PER LOD.
 *
 * At 15 m every helmet and weapon head is readable; at 40 m a halberd and a
 * spear are three pixels apart. Merging archetypes as they recede costs the
 * viewer nothing and removes a draw call and a shadow draw each time. The
 * banner-bearer is deliberately NEVER collapsed — a standard is legible from
 * the far side of the map and is what makes the mass read as an army.
 */
export const COLLAPSE = [
  [0, 1, 2, 3, 4, 5, 6, 7],   // LOD0 — everything distinct
  [0, 1, 2, 3, 4, 6, 6, 6],   // LOD1 — the named ranks share a body
  [0, 0, 0, 3, 4, 6, 6, 6],   // LOD2 — one infantryman, one bannerman, one archer
];

/**
 * Weapon socket rotations used by the CROWD only (the hero keeps the hand-tuned
 * ones from chars/weapons.js). Solved so the shaft stands upright in the
 * battle-ready idle: a field of vertical spears and standards is the Dynasty
 * Warriors silhouette, and the default sockets lay them out flat like fishing
 * rods once the crowd's guard pose raises the hand to chest height.
 *
 * These stay in the frame the hand was authored in: the shoulder-clearance
 * solve in anim/clips.js abducts every hanging arm to get the limb out of the
 * cuirass, and its wrist term cancels that rotation at the fist, so the carry
 * angle a weapon is given here is the carry angle it keeps.
 */
export const CROWD_SOCKET_ROT = {
  spear: [0.894, -0.991, -1.690],
  halberd: [0.875, -0.964, -1.683],
  banner: [0.863, -1.103, -1.857],
  greatGlaive: [1.014, -0.908, -1.442],
  dao: [1.117, -0.718, -1.081],
  bow: [0.825, 0.945, 1.725],
};
/** How much per-archetype lean to fan into a rank so it is not a picket fence. */
const JITTER = { banner: 0.05, spear: 0.16, halberd: 0.16, dao: 0.24, bow: 0.10, greatGlaive: 0.14 };

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
const _p = new THREE.Vector3(), _s = new THREE.Vector3();

/** Rigid-skin a loose geometry to one bone and move it to its bind-pose place. */
function rigidAttach(geo, rig, boneName, socket, extraScale = 1) {
  const g = geo.clone();
  const bi = BONE_INDEX[boneName];
  const bx = rig.world[bi * 3], by = rig.world[bi * 3 + 1], bz = rig.world[bi * 3 + 2];
  const sp = socket.pos || [0, 0, 0], sr = socket.rot || [0, 0, 0];
  _e.set(sr[0] || 0, sr[1] || 0, sr[2] || 0, 'YXZ');
  _q.setFromEuler(_e);
  const sc = (socket.scale ?? 1) * extraScale;
  _p.set(bx + sp[0], by + sp[1], bz + sp[2]);
  _s.setScalar(sc);
  _m.compose(_p, _q, _s);
  g.applyMatrix4(_m);
  const n = g.attributes.position.count;
  const si = new Uint16Array(n * 4), sw = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) { si[i * 4] = bi; sw[i * 4] = 1; }
  g.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
  g.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));
  if (!g.attributes.normal) g.computeVertexNormals();
  if (!g.attributes.color) {
    const c = new Float32Array(n * 3).fill(1);
    g.setAttribute('color', new THREE.BufferAttribute(c, 3));
  }
  return g;
}

/** Same attribute set, same order, or mergeGeometries refuses. */
function conform(g, names) {
  const out = new THREE.BufferGeometry();
  for (const n of names) {
    const a = g.attributes[n];
    if (!a) return null;
    out.setAttribute(n, a);
  }
  out.setIndex(g.index);
  return out;
}

/**
 * Build one archetype LOD: body geometry + baked weapon.
 * @param {object} chars   world.chars
 * @param {object} arch    an entry of ARCHES
 * @param {number} lod     0..2
 */
export function buildArchGeometry(chars, arch, lod) {
  const body = chars.variantGeo(arch.kind, arch.geo, lod, 0);
  const S = specFor(arch.kind, arch.geo);
  const rig = makeRig(S);
  const hScale = rig.height / REF_HEIGHT;
  const parts = [body];
  const wg = chars.weapons?.geo?.[arch.weapon];
  const sock = chars.weapons?.socket?.[arch.weapon];
  if (wg && sock) {
    const base = CROWD_SOCKET_ROT[arch.weapon] || sock.rot;
    const j = ((arch.geo * 37 + 11) % 7) / 7 - 0.5;
    const k = JITTER[arch.weapon] ?? 0.16;
    const s2 = Object.assign({}, sock);
    s2.rot = [base[0] + j * k, base[1] + j * k * 0.5, base[2] + j * k * 0.7];
    parts.push(rigidAttach(wg, rig, sock.bone || 'handR', s2, hScale));
  }
  const names = ['position', 'normal', 'uv', 'color', 'skinIndex', 'skinWeight'];
  const conformed = parts.map(g => conform(g, names)).filter(Boolean);
  let merged;
  if (conformed.length === 1) {
    merged = conformed[0];
  } else {
    merged = mergeGeometries(conformed, false);
    if (!merged) merged = conformed[0];
  }
  merged.computeBoundingSphere();
  merged.computeBoundingBox();
  if (merged.boundingSphere) merged.boundingSphere.radius *= 1.3;
  merged.userData.triangles = (merged.index ? merged.index.count : merged.attributes.position.count) / 3;
  merged.userData.arch = arch.key;
  merged.userData.lod = lod;
  merged.userData.rigHeight = rig.height;
  return merged;
}
